import { api } from '@/config/api';

export interface EmailSequenceStep {
  stepUid: string;
  order: number;
  delayDays: number;
  templateUid?: string;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
}

export interface EmailSequence {
  sequenceUid: string;
  companyUid: string;
  name: string;
  description?: string;
  accountUid?: string;
  status: 'draft' | 'active' | 'paused';
  pauseOnReply: boolean;
  steps: EmailSequenceStep[];
  totalEnrolled: number;
  totalSent: number;
  totalReplied: number;
  totalCompleted: number;
  createdByUserUid: string;
  createdAt: string;
}

export interface EmailSequenceEnrolment {
  enrolmentUid: string;
  sequenceUid: string;
  contactUid?: string;
  leadUid?: string;
  recipientEmail: string;
  recipientName?: string;
  status: 'active' | 'paused' | 'completed' | 'cancelled' | 'replied' | 'bounced';
  currentStepIndex: number;
  nextSendAt?: string;
  lastSentAt?: string;
  enrolledAt?: string;
  completedAt?: string;
}

export interface CreateSequenceInput {
  name: string;
  description?: string;
  accountUid?: string;
  pauseOnReply?: boolean;
  steps: Array<Omit<EmailSequenceStep, 'stepUid' | 'order'> & { order?: number }>;
}

export interface UpdateSequenceInput {
  name?: string;
  description?: string;
  accountUid?: string;
  pauseOnReply?: boolean;
  status?: 'draft' | 'active' | 'paused';
  steps?: Array<Omit<EmailSequenceStep, 'stepUid' | 'order'> & { order?: number; stepUid?: string }>;
}

const BASE = '/slighter_crm/email-sequences';

export const emailSequencesService = {
  list: async (): Promise<EmailSequence[]> => (await api.get(BASE)).data,
  get: async (sequenceUid: string): Promise<EmailSequence> => (await api.get(`${BASE}/${sequenceUid}`)).data,
  create: async (dto: CreateSequenceInput): Promise<EmailSequence> => (await api.post(BASE, dto)).data,
  update: async (sequenceUid: string, dto: UpdateSequenceInput): Promise<EmailSequence> =>
    (await api.patch(`${BASE}/${sequenceUid}`, dto)).data,
  remove: async (sequenceUid: string): Promise<void> => (await api.delete(`${BASE}/${sequenceUid}`)).data,

  // Enrolments
  enrolByEmails: async (
    sequenceUid: string,
    recipients: Array<{ email: string; name?: string; contactUid?: string }>,
  ): Promise<{ enrolled: number; skipped: number }> =>
    (await api.post(`${BASE}/${sequenceUid}/enrol/emails`, { recipients })).data,

  enrolFromGroup: async (
    sequenceUid: string,
    groupUid: string,
  ): Promise<{ enrolled: number; skipped: number }> =>
    (await api.post(`${BASE}/${sequenceUid}/enrol/group`, { groupUid })).data,

  listEnrolments: async (sequenceUid: string, limit = 200): Promise<EmailSequenceEnrolment[]> =>
    (await api.get(`${BASE}/${sequenceUid}/enrolments`, { params: { limit } })).data,

  pauseEnrolment: async (enrolmentUid: string): Promise<void> =>
    (await api.post(`${BASE}/enrolments/${enrolmentUid}/pause`)).data,
  resumeEnrolment: async (enrolmentUid: string): Promise<void> =>
    (await api.post(`${BASE}/enrolments/${enrolmentUid}/resume`)).data,
  cancelEnrolment: async (enrolmentUid: string): Promise<void> =>
    (await api.post(`${BASE}/enrolments/${enrolmentUid}/cancel`)).data,
};
